import React from 'react'
import Section from '../../common/section/section'
import { MembershipPayment, MembershipPaymentStatus } from 'memberships/schema'
import { formatISOString, DateFormats } from 'utils/dates'
import PermissionRequired from 'admin/permissionRequired'
import Currency from 'uiComponents/money/currency'
import { LoaderWrapper } from 'uiComponents/loaders'
import Badge, { BadgeVariant } from 'uiComponents/badge'
import './payments.scss'

const PaymentStatusMap: Record<MembershipPaymentStatus, { text: string; variant: BadgeVariant }> = {
    [MembershipPaymentStatus.PAID]: { text: 'Succeeded', variant: 'success' },
    [MembershipPaymentStatus.REFUNDED]: { text: 'Refunded', variant: 'info' },
    [MembershipPaymentStatus.REFUND_IN_PROGRESS]: { text: 'Refunding...', variant: 'info' },
    [MembershipPaymentStatus.PENDING]: { text: 'Pending', variant: 'warning' },
    [MembershipPaymentStatus.OPEN]: { text: 'Open', variant: 'warning' },
    [MembershipPaymentStatus.FAILED]: { text: 'Failed', variant: 'error' },
    [MembershipPaymentStatus.CHARGED_BACK]: { text: 'Charged Back', variant: 'error' },
    [MembershipPaymentStatus.CANCELLED]: { text: 'Cancelled', variant: 'neutral' },
}

const PaymentStatus = ({ status, loading }: { status: MembershipPaymentStatus; loading?: boolean }) => {
    const { text, variant } = PaymentStatusMap[status]

    return (
        <Badge variant={variant} loading={loading}>
            {text}
        </Badge>
    )
}

interface PaymentsSectionProps {
    payments: MembershipPayment[]
    accountSlug: string
    isLoading: boolean
    setPaymentToRefund: (payment: MembershipPayment) => void
}

const PaymentsSection = ({ payments, accountSlug, isLoading, setPaymentToRefund }: PaymentsSectionProps) => {
    const sortedPayments = [...payments].sort(
        (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime(),
    )

    return (
        <Section title="Payments" className="membership-details-payments-section">
            <table className="membership-details-payments-table">
                <thead>
                    <tr>
                        <th className="status">Status</th>
                        <th className="amount">Amount</th>
                        <th className="date">Date</th>
                        <th className="action" />
                    </tr>
                </thead>
                <tbody>
                    <LoaderWrapper loading={isLoading}>
                        {sortedPayments.map((payment) => (
                            <tr key={payment.paymentDate}>
                                <td className="status">
                                    <PaymentStatus status={payment.status} loading={isLoading} />
                                </td>
                                <td className="amount">
                                    <Currency amount={payment.amount} currency={payment.currency} />
                                </td>
                                <td className="date">{formatISOString(payment.paymentDate, DateFormats.SHORT_DATE)}</td>
                                <td className="action">
                                    {payment.status === 'PAID' && (
                                        <PermissionRequired name="refund_orders" accountSlug={accountSlug}>
                                            <div className="refund-button" onClick={() => setPaymentToRefund(payment)}>
                                                Refund
                                            </div>
                                        </PermissionRequired>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </LoaderWrapper>
                </tbody>
            </table>
        </Section>
    )
}

export default PaymentsSection
